import { FC, useMemo } from 'react'
import { MarketingMaterialsContextProvider, VisualsMarketingContextProvider } from 'components/pages/VisualsMarketing/_main/contexts'

import { AnalyticsEvent } from 'utils/analytics'
import { ClientPropertyAITextGenerator } from '../../ClientPropertyAITextGenerator'
import { ClientPropertyEmptyContent } from '../../ClientPropertyEmptyContent'
import { GRAY_800 } from 'constants/styling/theme'
import { PageTransition } from 'utils/animations'
import { Path } from 'constants/router'
import { RealEstatePropertyAssignmentDTO } from 'models/property'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import { VisualsMarketingFormProvider } from 'components/pages/VisualsMarketing/_main/contexts/VisualsMarketing.form'
import { useClientProperty } from '../contexts'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const ClientPropertyAITextGeneratorPage: FC = () => {

  const navigate = useNavigate()
  const { t } = useTranslation(['property'])
  const { hasUserActiveSubscription } = useUserData()
  const { propertyAssignmentsData, propertyFullAddress, handleClickOnEmptyAction } = useClientProperty()

  // The default orderId is used to identify the marketing providers. Note: This might be updated to use propertyId in the future.
  const defaultOrderId = useMemo(() => {
    if (!propertyAssignmentsData || propertyAssignmentsData?.length === 0) return null
    // Sort the propertyAssignmentsData by the smallest orderId and keep the first one
    const sortedOrderId: RealEstatePropertyAssignmentDTO = propertyAssignmentsData.reduce((acc, assignment) => {
      return acc.orderId < assignment.orderId ? acc : assignment
    }, propertyAssignmentsData[0])

    return sortedOrderId?.orderId ?? null
  }, [propertyAssignmentsData])

  // No content if the user doesn't have an active subscription
  if (!hasUserActiveSubscription) return null

  // Display empty content if there are no orders
  if (!defaultOrderId) {
    return (
      <ClientPropertyEmptyContent
        title={t('empty_text_generator.title')}
        description={t('empty_text_generator.description')}
        icon={<ShoppingCartOutlinedIcon sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
        buttonText={t('empty_text_generator.button')}
        onButtonClick={() => {
          navigate(Path.PURCHASE_FLOW)
          handleClickOnEmptyAction(AnalyticsEvent.PROPERTY_ORDER_VISUALS_CLICKED)
        }}
      />
    )
  }

  return (
    <PageTransition>

      <VisualsMarketingFormProvider id={defaultOrderId} address={propertyFullAddress}>
        <MarketingMaterialsContextProvider dealId={defaultOrderId}>
          <VisualsMarketingContextProvider>

            <ClientPropertyAITextGenerator />

          </VisualsMarketingContextProvider>
        </MarketingMaterialsContextProvider>
      </VisualsMarketingFormProvider>

    </PageTransition>
  )
}
